// Theme utility functions for dynamic site theming

/**
 * Convert hex color to RGB values
 * @param hex - Hex color string
 * @returns RGB values as comma-separated string
 */
const hexToRgb = (hex: string): string => {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

export interface ColorShades {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

export interface SiteTheme {
  primary: ColorShades;
  secondary: ColorShades;
  accent: ColorShades;
  quaternary?: ColorShades;
  quinary?: ColorShades;
}

/**
 * Get a theme color by name and shade
 * @param colorName - The color name (primary, secondary, accent)
 * @param shade - The shade (50-950)
 * @param theme - The site theme object
 * @returns The hex color value
 */
export const getThemeColor = (
  colorName: keyof SiteTheme,
  shade: keyof SiteTheme['primary'],
  theme: SiteTheme
): string => {
  const colorBlock = theme[colorName];
  if (!colorBlock) return '#000000';
  return colorBlock[shade] || '#000000';
};

/**
 * Generate CSS custom properties from theme
 * @param theme - The site theme object
 * @returns Object with CSS custom property names and values
 */
export const generateCSSCustomProperties = (theme: SiteTheme) => {
  const properties: Record<string, string> = {};
  
  // Generate all primary shades
  Object.entries(theme.primary).forEach(([shade, color]) => {
    properties[`--color-primary-${shade}`] = color;
  });
  
  // Generate all secondary shades
  Object.entries(theme.secondary).forEach(([shade, color]) => {
    properties[`--color-secondary-${shade}`] = color;
  });
  
  // Generate all accent shades
  Object.entries(theme.accent).forEach(([shade, color]) => {
    properties[`--color-accent-${shade}`] = color;
  });

  // Generate all quaternary shades (if present)
  if (theme.quaternary) {
    Object.entries(theme.quaternary).forEach(([shade, color]) => {
      properties[`--color-quaternary-${shade}`] = color;
    });
  }

  // Generate all quinary shades (if present)
  if (theme.quinary) {
    Object.entries(theme.quinary).forEach(([shade, color]) => {
      properties[`--color-quinary-${shade}`] = color;
    });
  }

  // Add main color variables for compatibility
  // Use the brand color (usually at 600 for primary sports colors)
  properties['--color-primary'] = theme.primary[600] || theme.primary[500];
  properties['--color-primary-dark'] = theme.primary[700] || theme.primary[600];
  properties['--color-primary-light'] = theme.primary[500] || theme.primary[400];
  properties['--color-secondary'] = theme.secondary[500];
  properties['--color-secondary-dark'] = theme.secondary[600];
  properties['--color-secondary-light'] = theme.secondary[400];
  properties['--color-accent'] = theme.accent[500];
  properties['--color-accent-dark'] = theme.accent[600];
  properties['--color-accent-light'] = theme.accent[400];

  // Add quaternary and quinary color variables if present
  if (theme.quaternary) {
    properties['--color-quaternary'] = theme.quaternary[500];
    properties['--color-quaternary-dark'] = theme.quaternary[600];
    properties['--color-quaternary-light'] = theme.quaternary[400];
  }

  if (theme.quinary) {
    properties['--color-quinary'] = theme.quinary[500];
    properties['--color-quinary-dark'] = theme.quinary[600];
    properties['--color-quinary-light'] = theme.quinary[400];
  }

  // Generate gradients using the actual brand colors
  properties['--gradient-primary'] = `linear-gradient(135deg, ${theme.primary[600] || theme.primary[500]} 0%, ${theme.secondary[500]} 100%)`;
  properties['--gradient-secondary'] = `linear-gradient(135deg, ${theme.secondary[500]} 0%, ${theme.accent[500]} 100%)`;
  properties['--gradient-accent'] = `linear-gradient(135deg, ${theme.accent[500]} 0%, ${theme.primary[600] || theme.primary[500]} 100%)`;

  // Generate gradient color stops for Tailwind gradient utilities
  properties['--gradient-from-primary'] = theme.primary[600] || theme.primary[500];
  properties['--gradient-from-secondary'] = theme.secondary[500];
  properties['--gradient-from-accent'] = theme.accent[500];
  properties['--gradient-to-primary'] = theme.primary[700] || theme.primary[600];
  properties['--gradient-to-secondary'] = theme.secondary[600];
  properties['--gradient-to-accent'] = theme.accent[600];

  // Generate neon text colors and shadows - use a brighter shade for neon effect
  // For sports sites, use secondary (often the brighter color) for neon
  properties['--neon-color'] = theme.secondary[500] || theme.primary[400];
  properties['--neon-color-rgb'] = hexToRgb(theme.secondary[500] || theme.primary[400]);
  properties['--neon-shadow'] = `
    0 0 10px rgba(${hexToRgb(theme.secondary[500] || theme.primary[400])}, 0.8),
    0 0 20px rgba(${hexToRgb(theme.secondary[500] || theme.primary[400])}, 0.6),
    0 0 30px rgba(${hexToRgb(theme.secondary[500] || theme.primary[400])}, 0.4)
  `.trim();

  // Generate RGB versions for colors (for use in rgba())
  properties['--color-primary-rgb'] = hexToRgb(theme.primary[600] || theme.primary[500]);
  properties['--color-secondary-rgb'] = hexToRgb(theme.secondary[500]);
  properties['--color-accent-rgb'] = hexToRgb(theme.accent[500]);

  if (theme.quaternary) {
    properties['--color-quaternary-rgb'] = hexToRgb(theme.quaternary[500]);
  }

  if (theme.quinary) {
    properties['--color-quinary-rgb'] = hexToRgb(theme.quinary[500]);
  }

  // Generate sport-specific gradient backgrounds
  properties['--gradient-sport-primary'] = `linear-gradient(135deg, ${theme.primary[500]}20 0%, ${theme.primary[600]}20 100%)`;
  properties['--gradient-sport-secondary'] = `linear-gradient(135deg, ${theme.secondary[500]}20 0%, ${theme.secondary[600]}20 100%)`;
  properties['--gradient-sport-accent'] = `linear-gradient(135deg, ${theme.accent[500]}20 0%, ${theme.accent[600]}20 100%)`;

  // Border colors with opacity
  properties['--border-primary'] = `${theme.primary[500]}30`;
  properties['--border-secondary'] = `${theme.secondary[500]}30`;
  properties['--border-accent'] = `${theme.accent[500]}30`;

  return properties;
};

/**
 * Apply theme to document root
 * @param theme - The site theme object
 */
export const applyThemeToDocument = (theme: SiteTheme) => {
  const properties = generateCSSCustomProperties(theme);
  
  Object.entries(properties).forEach(([property, value]) => {
    document.documentElement.style.setProperty(property, value);
  });
};

/**
 * Get theme-aware Tailwind classes
 * @param baseClasses - Base Tailwind classes
 * @param theme - The site theme object
 * @returns Modified classes with theme colors
 */
export const getThemeClasses = (baseClasses: string, theme?: SiteTheme): string => {
  if (!theme) return baseClasses;
  
  // This could be extended to dynamically replace color classes
  // For now, we rely on CSS custom properties
  return baseClasses;
};

/**
 * Generate site-specific color palette for sport themes
 * @param sport - The sport type
 * @param level - The level type
 * @returns A theme object optimized for the sport/level combination
 */
export const generateSportTheme = (sport: string | null, level: string | null): Partial<SiteTheme> => {
  // This is a simplified version - the full implementation is in useSiteConfig
  // This function could be used for dynamic theme generation
  
  const sportColorMap: Record<string, { primary: string; secondary: string; accent: string }> = {
    football: { primary: '#013369', secondary: '#D50A0A', accent: '#FFB81C' },
    basketball: { primary: '#C9082A', secondary: '#17408B', accent: '#FDB927' },
    baseball: { primary: '#002D72', secondary: '#D50032', accent: '#FDB827' },
    hockey: { primary: '#111111', secondary: '#C8102E', accent: '#FCB514' },
  };
  
  if (sport && sportColorMap[sport]) {
    const colors = sportColorMap[sport];
    return {
      primary: { 500: colors.primary } as any,
      secondary: { 500: colors.secondary } as any,
      accent: { 500: colors.accent } as any,
    };
  }
  
  return {};
};

/**
 * Check if current theme is dark or light based on primary color
 * @param theme - The site theme object
 * @returns true if theme is considered dark
 */
export const isThemeDark = (theme: SiteTheme): boolean => {
  // Simple luminance check on primary color
  const hex = theme.primary[500].replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance < 0.5;
};

/**
 * Get contrast color (white or black) based on background color
 * @param backgroundColor - The background color hex value
 * @returns '#ffffff' or '#000000' based on contrast
 */
export const getContrastColor = (backgroundColor: string): string => {
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#ffffff';
};